<script setup>
import { ref, computed, onMounted, watch, nextTick, inject } from 'vue'
import { closeOffCanvas as closeBootstrapOffCanvas } from '../utils/offcanvas'
import { useEmployerStore } from '../stores/EmployerStore'
import { useBusinessDaysReminder } from '../composables/useBusinessDaysReminder'
import { RemindersCalendarService } from '../services/RemindersCalendarService'
import { storeToRefs } from 'pinia'
import BcsTooltip from './BcsTooltip.vue'
import { RequestService } from '../services/RequestService'
import { AgencyDataService } from '../services/AgencyDataService'
import ReminderModal from './Reminders/ReminderModal.vue'
import { v4 as uuidv4 } from 'uuid'
import { 
  formatDateToSendReminder,
  getTodayDate,
  parseLocalDate,
  formatLocalDate
} from './Reminders/dateUtils'
import { withEmployerNameInInvite } from './Reminders/helpers'
import { useQuestionsStore } from '../stores/QuestionsStore'

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const props = defineProps({
  allowedDates: {
    type: Array,
    default: () => []
  },
  startDate: {
    type: Date,
    required: false,
    default: () => new Date()
  },
  endDate: {
    type: Date,
    required: false,
    default: () => new Date()
  }
})

const emit = defineEmits(['dateSelected', 'nextReminders', 'dateRange', 'calendarType', 'reminderSent'])

const emitter = inject('emitter')

// Handle event-clicked from BcsTooltip in calendar
const handleTooltipEvent = ({ event }) => {
  emitter.emit('toggleOffCanvas', {
    id: event.body,
    definitionExpected: event.name,
    date: event.date,
    source: 'calendar'
  })
}

const employerStore = useEmployerStore()
const questionStore = useQuestionsStore()

const { getReminders, getEvents, postReminders, getGoogleReminders } = RemindersCalendarService()
const { handleErrors, createLog } = RequestService()
const { actualPlanYear, actualEmployer, userData, actualPlanYearId } = storeToRefs(employerStore)

const currentMonth = ref('')
const remindersMonths = ref()
const filteredReminders = ref([])
const nextEvents = ref([])
const nextEventsPlanYear = ref([])
const year = ref()
const endOfPlanYear = ref()
const calendarDays = ref([])
const activeTooltipDate = ref(null)
const calendarType = ref()

const eventDefinition = ref(null)
const events = ref([])
const reminderModalRef = ref(null)
const modalReminderSource = ref('')
const reminderSendSucess = ref(false)
const recorderReminderSend = ref([])
const googleReminders = ref([])
const justSentReminders = ref([])
const yearOfActualPlan = ref('')
const todayDate = ref(getTodayDate())

const planCompleted = computed(() => {
  return questionStore.isPlanComplete()
})

const { 
  reminderDaysOption, 
  subtractBusinessDays 
} = useBusinessDaysReminder(eventDefinition, actualPlanYear, yearOfActualPlan)

const dateUtils = {
  getMonday(date) {
    const day = date.getDay()
    const diff = date.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(date.setDate(diff))
  },

  formatDate(dateString) {
    const [month, day] = dateString.split('/').map(Number)
    const date = new Date(2000, month - 1, day)
    const monthAbbr = date.toLocaleString('en-US', { month: 'short' })
    return `${monthAbbr} ${day.toString().padStart(2, '0')}`
  },

  convertDate(dateString) {
    if (!actualPlanYear.value?.plan_start_date) {
      handleErrors('Calendar at a glance - convertDate', new Error('actualPlanYear or plan_start_date is undefined'))
      return new Date()
    }
    const [month, day] = dateString.split('/').map(Number)
    const [planMonth, planDay, planYear] = actualPlanYear.value.plan_start_date
      .split('/')
      .map(Number)
    const date = new Date(planYear, month - 1, day)
    const planStartDate = new Date(planYear, planMonth - 1, planDay)
    if (date < planStartDate) {
      date.setFullYear(planYear + 1)
    }
    return date
  }
}

const effectiveStartDate = computed(() => {
  if (!actualPlanYear.value?.plan_start_date) {
    return props.startDate
  }
  const [month, day, year] = actualPlanYear.value.plan_start_date.split('/').map(Number)
  const planDate = new Date(year, month - 1, day)
  const today = new Date()
  return planDate <= today ? today : planDate
})

const effectiveEndDate = computed(() => {
  const planStartDateStr = actualPlanYear.value?.plan_start_date
  if (!planStartDateStr) {
    return props.endDate
  }
  const [month, day, year] = planStartDateStr.split('/').map(Number)
  const planStartDate = new Date(year, month - 1, day)
  const today = new Date()
  const diffDays = Math.floor((endOfPlanYear.value - today) / (1000 * 60 * 60 * 24))

  let end
  if (endOfPlanYear.value < today) {
    [calendarType.value, end] = [4, new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())]
  } else if (planStartDate <= today && diffDays < 30 && endOfPlanYear.value >= today) {
    [calendarType.value, end] = [3, endOfPlanYear.value]
  } else if (today > planStartDate) {
    [calendarType.value, end] = [1, new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())]
  } else if (planStartDate > today) {
    [calendarType.value, end] = [2, new Date(year, month, day)]
  }
  return end
})

const dateRangeTitle = computed(() => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  })

  emit('dateRange', {
    startDate: effectiveStartDate.value,
    endDate: effectiveEndDate.value,
    endOfPlanYear: endOfPlanYear.value,
    calendarType: calendarType.value
  })

  const startFormatted = formatter.format(effectiveStartDate.value)
  const endFormatted = formatter.format(effectiveEndDate.value)
  const startYear = effectiveStartDate.value.getFullYear()
  const endYear = effectiveEndDate.value.getFullYear()
  return endYear > startYear ?
      `${startFormatted}, ${startYear} - ${endFormatted}, ${endYear}`
    : `${startFormatted} - ${endFormatted}, ${endYear}`
})

const daysWithTooltip = computed(() => {
  return calendarDays.value.map((day) => ({
    ...day,
    needsTooltip: day.hasEvents
  }))
})

const filteredEvents = computed(() => {
  let today = new Date()
  today.setHours(0, 0, 0, 0)
  const getEventDate = (eventDateStr, referenceYear, planMonth, planDay) => {
    const [eventMonth, eventDay] = eventDateStr.split('/').map(Number)
    let eventYear = referenceYear

    if (eventMonth < planMonth || (eventMonth === planMonth && eventDay < planDay)) {
      eventYear++
    }

    return new Date(eventYear, eventMonth - 1, eventDay)
  }

  if (actualPlanYear.value && actualPlanYear.value.plan_start_date) {
    const [planMonth, planDay, planYear] = actualPlanYear.value.plan_start_date
      .split('/')
      .map(Number)
    const planStartDate = new Date(planYear, planMonth - 1, planDay)
    today = planStartDate >= today ? planStartDate : today

    const filtered = events.value.filter((event) => {
      const eventDate = getEventDate(event.date, planYear, planMonth, planDay)
      return eventDate >= today
    })

    return filtered.sort((a, b) => {
      const dateA = getEventDate(a.date, planYear, planMonth, planDay)
      const dateB = getEventDate(b.date, planYear, planMonth, planDay)
      return dateA - dateB
    })
  } else {
    const filtered = events.value.filter((event) => {
      const [month, day] = event.date.split('/').map(Number)
      const eventDate = new Date(today.getFullYear(), month - 1, day)
      return eventDate >= today
    })

    return filtered.sort((a, b) => {
      const [monthA, dayA] = a.date.split('/').map(Number)
      const [monthB, dayB] = b.date.split('/').map(Number)
      const dateA = new Date(today.getFullYear(), monthA - 1, dayA)
      const dateB = new Date(today.getFullYear(), monthB - 1, dayB)
      return dateA - dateB
    })
  }
})

const calendarLogic = {
  isDateInRange(date) {
    const startOfDay = new Date(date)
    startOfDay.setHours(0, 0, 0, 0)
    const comparableStart = new Date(effectiveStartDate.value)
    comparableStart.setHours(0, 0, 0, 0)
    const comparableEnd = new Date(effectiveEndDate.value)
    comparableEnd.setHours(23, 59, 59, 999)
    return startOfDay >= comparableStart && startOfDay <= comparableEnd
  },

  isDateAllowed(date) {
    return (
      this.isDateInRange(date) &&
      props.allowedDates.some(
        (allowedDate) =>
          allowedDate.getDate() === date.getDate() &&
          allowedDate.getMonth() === date.getMonth() &&
          allowedDate.getFullYear() === date.getFullYear()
      )
    )
  },

  getDayClasses(day) {
    const normalizedDayDate = new Date(day.date)
    normalizedDayDate.setHours(0, 0, 0, 0)
    const normalizedStartDate = new Date(effectiveStartDate.value)
    normalizedStartDate.setHours(0, 0, 0, 0)
    const isStartDate = normalizedDayDate.getTime() === normalizedStartDate.getTime()
    return {
      'day-cell': true,
      inactive: !day.isInRange,
      active: day.isInRange,
      allowed: day.isAllowed && day.isInRange,
      today: isStartDate,
      'event-reminder': day.hasEvents
    }
  }
}

const getRemindersByDate = (date) => {
  if (!date || !filteredReminders.value) return []
  return filteredReminders.value.filter((reminder) => {
    const reminderDate = dateUtils.convertDate(reminder.date)
    return (
      reminderDate.getDate() === date.getDate() &&
      reminderDate.getMonth() === date.getMonth() &&
      reminderDate.getFullYear() === date.getFullYear()
    )
  })
}

const generateCalendarDays = () => {
  let days = []
  const start = new Date(effectiveStartDate.value)
  const end = new Date(effectiveEndDate.value)
  end.setHours(23, 59, 59, 999)
  let currentDate = dateUtils.getMonday(new Date(start))
  while (currentDate <= end) {
    const dateToAdd = new Date(currentDate)
    const events = getRemindersByDate(dateToAdd)
    days.push({
      date: dateToAdd,
      isCurrentMonth: dateToAdd.getMonth() === start.getMonth(),
      isInRange: calendarLogic.isDateInRange(dateToAdd),
      isAllowed: calendarLogic.isDateAllowed(dateToAdd),
      events: events,
      hasEvents: events.length > 0
    })
    currentDate.setDate(currentDate.getDate() + 1)
  }
  calendarDays.value = days
}

const reminderHandler = {
  async updateMonths() {
    const numberStartMonth = parseInt(actualPlanYear.value?.plan_start_date.split('/')[0]) - 1
    if (planCompleted.value) {
      remindersMonths.value = await getReminders(numberStartMonth, actualPlanYear.value?.groupInsuranceType)
    } else {
      remindersMonths.value = {}
    }
  },

  extractEvents(remindersMonths) {
    if (!remindersMonths) return
    filteredReminders.value = []
    nextEvents.value = []
    const startDate = new Date(effectiveStartDate.value)
    startDate.setHours(0, 0, 0, 0)
    endOfPlanYear.value = new Date(actualPlanYear.value?.plan_start_date)
    endOfPlanYear.value.setFullYear(endOfPlanYear.value.getFullYear() + 1)
    endOfPlanYear.value.setDate(endOfPlanYear.value.getDate() - 1)
    const endDate = new Date(effectiveEndDate.value)
    endDate.setHours(23, 59, 59, 999)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    Object.values(remindersMonths).forEach((month) => {
      Object.values(month).forEach((week) => {
        if (Array.isArray(week)) {
          week.forEach((item) => {
            if (typeof item === 'object' && item !== null && 'id' in item && 'type' in item) {
              const itemDate = dateUtils.convertDate(item.date)
              itemDate.setHours(0, 0, 0, 0)
              filteredReminders.value.push(item)
              const isInRange =
                itemDate.getTime() >= startDate.getTime() &&
                itemDate.getTime() <= endDate.getTime()
              if (isInRange) {
                nextEvents.value.push({
                  ...item,
                  formatDate: dateUtils.formatDate(item.date)
                })
              }

              const isInRangePlanYear =
                itemDate.getTime() >= today.getTime() &&
                itemDate.getTime() <= endOfPlanYear.value.getTime()
              if (isInRangePlanYear) {
                nextEventsPlanYear.value = nextEventsPlanYear.value || []
                nextEventsPlanYear.value.push({
                  ...item,
                  formatDate: dateUtils.formatDate(item.date)
                })
              }
            }
          })
        }
      })
    })
    nextEvents.value.sort((a, b) => {
      const dateA = dateUtils.convertDate(a.date)
      const dateB = dateUtils.convertDate(b.date)
      return dateA - dateB
    })
    emit('nextReminders', { nextEventsMonth: nextEvents.value, nextEventsPlanYear: nextEventsPlanYear.value })
  },
  filterReminders() {
    this.extractEvents(remindersMonths.value)
    generateCalendarDays()
  }
}

const handleSendReminders = async (remindersData) => {
  try {    
    const { filteredEvents, eventDefinition } = remindersData
    
    emit('reminderSent', {
      success: true,
      reminders: justSentReminders.value
    })

    justSentReminders.value = []
    recorderReminderSend.value = []
    
    let eventsToProcess = []
    if (remindersData.modalReminderSource === 'OffCanvas') {
      eventsToProcess = [eventDefinition]
    } else {
      eventsToProcess = filteredEvents.filter(event => 
        event.selected === true && 
        event.reminder !== '' && 
        event.reminder !== null && 
        event.reminder !== undefined
      ) || []
    }
        
    for (const event of eventsToProcess) {
      let reminderDays
      if (remindersData.modalReminderSource === 'OffCanvas') {
        reminderDays = remindersData.selectedDays
        if (event.customReminder !== null && event.customReminder !== undefined) {
          reminderDays = event.customReminder
        }
      } else {
        if (event.reminder === -2 && event.customReminder !== null && event.customReminder !== undefined) {
          reminderDays = event.customReminder
        } else if (event.reminder !== '' && event.reminder !== -1 && event.reminder !== -2) {
          reminderDays = event.reminder
        } else if (event.reminder === 0) {
          reminderDays = 0
        } else {
          reminderDays = 0
        }
      }

      await sendSingleGoogleReminder(event, remindersData, reminderDays)
      
      const formattedDueDate = formatDateToSendReminder(event.date, actualPlanYear.value, yearOfActualPlan.value)
      
      let reminderDate
      if (reminderDays === 0) {
        reminderDate = todayDate.value
      } else {
        const dueDateObj = parseLocalDate(formattedDueDate)
        const reminderDateObj = subtractBusinessDays(dueDateObj, reminderDays)
        reminderDate = formatLocalDate(reminderDateObj) 
      }
      
      const allRecipients = [
        ...remindersData.selectedRecipients.map(r => r.email),
        ...remindersData.validEmails
      ].join(', ')
      
      justSentReminders.value.push({
        id: event.id || Date.now() + Math.random(),
        name: event.name,
        due_date: formattedDueDate,
        start_date: reminderDate,   
        created_at: new Date().toISOString(),
        target_emails: allRecipients,
        body: event.body || remindersData.descriptionEvent
      })
    }
        
    const freshGoogleReminders = (await getGoogleReminders(actualPlanYearId.value)).results || []
    googleReminders.value = freshGoogleReminders
        
    reminderSendSucess.value = true
    
    const DUE_DATE_REMINDER_SENT = '4'
    createLog({
      eventId: DUE_DATE_REMINDER_SENT,
      logName: 'user_activity_log_v2',
      employerId: actualEmployer.value.id,
      planYearId: actualPlanYearId.value,
      agencyId: actualEmployer.value.agency_id
    })

    if (reminderModalRef.value) {
      reminderModalRef.value.clearModal()
    }

    // Mark agency data as updated for Action Center cache invalidation
    const { markAgencyDataUpdated } = AgencyDataService()
    markAgencyDataUpdated(actualEmployer.value.agency_id)
    
  } catch (error) {
    handleErrors('handleSendReminders', error)
    reminderSendSucess.value = false
  }
}

const sendSingleGoogleReminder = async (eventInfo, remindersData, reminderDays) => {
  const endDate = formatDateToSendReminder(eventInfo.date, actualPlanYear.value, yearOfActualPlan.value)
  
  const transformedEmails = [
    ...remindersData.selectedRecipients,
    ...remindersData.validEmails
      .filter((email) => email.trim() !== '')
      .map((email) => ({ email: email.trim() }))
  ].filter((attendee) => attendee.email !== '')

  const daysToReminder = reminderDays
  const baseDescription = remindersData.descriptionEvent || eventInfo.body
  const inviteBody = `<b>Due Date: ${endDate}</b>\n\n${baseDescription}`
  const completeDescription = withEmployerNameInInvite(actualEmployer.value.name, inviteBody)

  let startDate
  if (daysToReminder == 0) {
    startDate = getTodayDate()
  } else {
    const endDateObj = parseLocalDate(endDate)
    const startDateObj = subtractBusinessDays(endDateObj, daysToReminder)
    startDate = formatLocalDate(startDateObj)
  }

  const body = {
    id: 'bcspro' + uuidv4().toLowerCase().replace(/-/g, ''),
    summary: eventInfo.name,
    description: completeDescription,
    end: { date: startDate },
    start: { date: startDate },
    attendees: transformedEmails,
    dueDate: endDate,
    employerId: actualEmployer.value.id,
    planYearId: actualPlanYearId.value
  }

  try {
    await postReminders(body)
    await createLog({
      logName: 'sent_reminder_log_v2',
      employerId: actualEmployer.value.id,
      planYearId: actualPlanYearId.value,
      agencyId: actualEmployer.value.agency_id,
      origin: 'bcsCalendar',
      role: userData.value.role || '',
      body
    })
    
    recorderReminderSend.value.push({
      name: body.summary,
      success: true
    })
  } catch (error) {
    handleErrors('sendSingleGoogleReminder', error)
    throw error
  }
}

const updateEvents = async () => {
  let numberStartMonth = parseInt(actualPlanYear.value?.plan_start_date.split('/')[0]) - 1
  events.value = (await getEvents(numberStartMonth, actualPlanYear.value?.groupInsuranceType)).map((event) => ({
    ...event,
    reminder: '',
    selected: false,
    customReminder: null,
    customDate: null,
    showCustom: false
  }))
}

const handleDateClick = (day) => {
  if (day.isAllowed && day.isInRange) {
    emit('dateSelected', day.date)
  }

  if (day.hasEvents) {
    if (activeTooltipDate.value && activeTooltipDate.value.getTime() === day.date.getTime()) {
      activeTooltipDate.value = null
    } else {
      activeTooltipDate.value = day.date
    }
  }
}

const closeOffCanvas = () => {
  try {
    activeTooltipDate.value = null
    closeBootstrapOffCanvas()
  } catch (error) {
    handleErrors('Error closing off-canvas from calendar at a glance:', error)
  }
}

function setupModalEventListeners() {
  nextTick(() => {
    const addDueDateModal = document.getElementById('addDueDateModal')
    if (addDueDateModal) {
      addDueDateModal.addEventListener('show.bs.modal', (event) => {
        const button = event.relatedTarget
        if (button) {
          const source = button.getAttribute('data-source')
          if (source === 'calendar') {
            modalReminderSource.value = 'calendar'
          } else if (source === 'offCanvas') {
            modalReminderSource.value = 'OffCanvas'
            
            closeOffCanvas()
          }
        }
        if (reminderModalRef.value) {
          reminderModalRef.value.clearModal()
        }
      })

      addDueDateModal.addEventListener('shown.bs.modal', () => {
        if (reminderModalRef.value && reminderModalRef.value.insertDefaultEmails) {
          reminderModalRef.value.insertDefaultEmails()
        }
      })

      addDueDateModal.addEventListener('hidden.bs.modal', () => {
        if (reminderModalRef.value) {
          reminderModalRef.value.clearModal()
        }
      })
    }
  })
}

onMounted(async () => {
  if (actualPlanYear.value?.plan_start_date) {
    const [month, day, year] = actualPlanYear.value.plan_start_date.split('/').map(Number);
    endOfPlanYear.value = new Date(year, month - 1, day);
    endOfPlanYear.value.setFullYear(endOfPlanYear.value.getFullYear() + 1);
    endOfPlanYear.value.setDate(endOfPlanYear.value.getDate() - 1);
    yearOfActualPlan.value = year.toString()
  }
  
  generateCalendarDays()
  if (actualPlanYear.value?.plan_start_date) {
    year.value = actualPlanYear.value?.plan_start_date?.split('/')[2] || ''
    await reminderHandler.updateMonths()
    await updateEvents()
  }
  currentMonth.value = new Date().getMonth()
  reminderHandler.filterReminders()

  setupModalEventListeners()
  
  if (emitter) {
    emitter.on('toggleOffCanvas', (data) => {
      activeTooltipDate.value = null
      
      const { id, definitionExpected, date } = data
      
      let foundEvent = events.value.find(event => 
        event.body === id && event.name === definitionExpected && event.date === date
      )
      
      if (!foundEvent) {
        foundEvent = filteredReminders.value.find(event => 
          event.body === id && event.name === definitionExpected && event.date === date
        )
      }
      
      if (!foundEvent) {
        foundEvent = {
          id: Date.now(),
          body: id,
          name: definitionExpected,
          date: date,
          type: 2,
          plan_start_month: 11,
          reminder: '',
          selected: false,
          customReminder: null,
          customDate: null,
          showCustom: false
        }
      }
      
      eventDefinition.value = foundEvent
    })
  } else {
    handleErrors('Emitter not available')
  }
})

watch(
  [() => actualPlanYear?.value?.plan_start_date, () => planCompleted.value],
  async ([newDate]) => {
    if (newDate) {
      generateCalendarDays()
      reminderHandler.extractEvents()
      await reminderHandler.updateMonths()
      await updateEvents()
      currentMonth.value = new Date().getMonth()
      reminderHandler.filterReminders()
    }
  }
)

watch([remindersMonths, filteredReminders], () => {
  nextTick(() => {
    generateCalendarDays()
  })
})
</script>

<template>
  <div class="custom-date-picker">
    <div class="date-picker-container">
      <div class="date-range-title text-14">
        {{ dateRangeTitle }}
      </div>

      <div class="weekdays-grid text-14">
        <div v-for="day in WEEK_DAYS" :key="day" class="weekday">
          {{ day }}
        </div>
      </div>

      <div class="days-grid text-14">
        <template v-for="day in daysWithTooltip" :key="day.date">
          <BcsTooltip
            v-if="day.needsTooltip"
            :content="[{ date: day.date, events: day.events }]"
            :triggerOnClick="true"
            :is-active="activeTooltipDate && day.date.getTime() === activeTooltipDate.getTime()"
            @tooltipOpen="status => { if (status === null) activeTooltipDate = null }"
            @event-clicked="handleTooltipEvent">
            <div
              class="text-14"
              :class="calendarLogic.getDayClasses(day)"
              @click.stop="handleDateClick(day)">
              {{ day.date.getDate() }}
            </div>
          </BcsTooltip>

          <div
            v-else
            class="text-14"
            :class="calendarLogic.getDayClasses(day)"
            @click="handleDateClick(day)">
            {{ day.date.getDate() }}
          </div>
        </template>
      </div>
    </div>

    <!-- REMINDER MODAL -->
    <ReminderModal
      ref="reminderModalRef"
      :eventDefinition="eventDefinition"
      :filteredEvents="filteredEvents"
      :modalReminderSource="modalReminderSource"
      :reminderDaysOption="reminderDaysOption"
      :sortDirectionEvents="'asc'"
      :recipients="userData.agencyEmails"
      :actualEmployer="actualEmployer"
      :actualPlanYear="actualPlanYear"
      :yearOfActualPlan="yearOfActualPlan"
      @sendReminders="handleSendReminders"
      @toggleSortEvents="() => {}"
      />
  </div>
</template>

<style lang="scss" scoped>
  .date-picker-container {
    padding: 15px 5px 10px 5px;
    margin: 0 auto;
    min-height: 229px;
  }

  .date-range-title {
    font-size: 14px;
    text-align: center;
    margin-bottom: 5px;
    font-weight: 400;
    color: #333;
  }

  .weekdays-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  .weekday {
    text-align: center;
    font-size: 14px;
    color: var(--bs-gray-700);
    font-weight: 400;
    padding: 0.5rem 0;
  }

  .days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    justify-items: center;
  }

  .day-cell {
    height: 1.5rem;
    margin-bottom: 4px;
    width: 1.7rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 400;
    cursor: default;
  }

  .day-cell.inactive {
    color: #d1d5db;
  }

  .day-cell.active {
    color: var(--bs-gray-700);
  }

  .day-cell.allowed {
    cursor: pointer;
  }

  .day-cell.allowed:hover {
    background-color: #f3f4f6;
    border-radius: 9999px;
  }

  .today {
    background-color: var(--bs-gray-300);
    border-radius: 50%;
    border: 1px solid var(--bs-gray-400);
  }

  .event-reminder {
    background-color: #fff6da;
    border-radius: 50%;
    color: var(--bs-primary) !important;
    font-weight: 700 !important;
    border: none !important;
    cursor: pointer;
  }

  .event-reminder:active {
    background-color: #ffe7b3;
  }
</style>